/**
 * IndividualReports — per-employee talent performance report.
 *
 * Lets users with the `individual:talent-map:self` permission view and
 * download their own competency evaluation history. Aggregates data from
 * the `evaluations` and `evaluation_ratings` tables in Supabase and renders
 * radar / bar charts for each competency group.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  User, ArrowLeft, ChevronDown, ChevronUp,
  Download, Search, FileText, Target,
} from 'lucide-react';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './IndividualReports.css';

/* ─── Static Configuration ─────────────────────────────────────────────────── */

const DEPARTMENTS = [
  { id: '', name: 'All Departments' },
  { id: 'finance', name: 'Finance' },
  { id: 'programs', name: 'Programs' },
  { id: 'business-development', name: 'Business Development' },
  { id: 'digital-health', name: 'Digital Health' },
  { id: 'people-culture', name: 'People and Culture' },
];

const QUARTERS = [
  { id: '', name: 'All Periods' },
  { id: 'q1', name: 'Q1 (Jan – Mar)' },
  { id: 'q2', name: 'Q2 (Apr – Jun)' },
  { id: 'q3', name: 'Q3 (Jul – Sep)' },
  { id: 'q4', name: 'Q4 (Oct – Dec)' },
];

const currentYear = new Date().getFullYear();
const YEARS = [
  { id: '', name: 'All Years' },
  ...Array.from({ length: 5 }, (_, i) => ({
    id: String(currentYear - i),
    name: String(currentYear - i),
  })),
];

const EVALUATION_CRITERIA = [
  {
    scale: 'Below 65%',
    label: 'Below Expectation',
    description: 'Performance is significantly lower than required – more than 25% below target',
    interpretation:
      'Struggles to deliver to expectation. Improvement is necessary in clearly isolatable areas, and targeted support may be required.',
    color: '#dc2626',
    bg: '#fef2f2',
  },
  {
    scale: 'Below 75%',
    label: 'Developing',
    description: 'Performance is lower than expected – up to 25% below target',
    interpretation:
      'Demonstrates progress but fails to meet expectations. Continued development is needed to reach full proficiency.',
    color: '#f59e0b',
    bg: '#fffbeb',
  },
  {
    scale: 'Below 90%',
    label: 'Satisfactory',
    description: 'Performance matches expectations – up to 25% above expected performance',
    interpretation: 'Consistently meets expectations; is competent and dependable.',
    color: '#3b82f6',
    bg: '#eff6ff',
  },
  {
    scale: 'Above 90%',
    label: 'Outstanding',
    description: 'Performance is significantly above required levels – 25% and beyond expectation',
    interpretation:
      'Consistently exceeds expectations; demonstrates exceptional initiative, quality, and impact. Is a role model for others.',
    color: '#10b981',
    bg: '#f0fdf4',
  },
];

const CORE_COMPETENCY_GROUPS = [
  {
    name: 'Strategic Outlook',
    competencies: [
      'Sensitivity to the Operating Environment',
      'Vision and Conceptualisation of Strategy',
      'Go-getter',
      'Numeracy',
    ],
  },
  {
    name: 'Innovation',
    competencies: [
      'Critical Thinking',
      'Digital Awareness',
      'Thought Leadership',
      'Continuous Improvement',
    ],
  },
  {
    name: 'Leadership',
    competencies: [
      'Making Things Happen',
      'Leading and Managing Change',
      'Managing People',
      'Communication',
    ],
  },
  {
    name: 'Performance Management',
    competencies: [
      'Focus on Outcomes',
      'Performance Planning',
      'Monitoring, Measuring, and Evaluating Performance',
      'Performance Review',
    ],
  },
  {
    name: 'Building and Maintaining Relationships',
    competencies: [
      'Teamwork and Collaboration',
      'Partnership and Strategic Alliance',
      'Managing Conflict',
      'Cultural Intelligence',
    ],
  },
];

/* ─── Helpers ───────────────────────────────────────────────────────────────── */

const calculateAverage = (scores) => {
  if (!scores || scores.length === 0) return 0;
  return Math.round(scores.reduce((acc, item) => acc + item.score, 0) / scores.length);
};

const getPerformanceLevel = (score) => {
  if (score < 65) return 'Below Expectation';
  if (score < 75) return 'Developing';
  if (score < 90) return 'Satisfactory';
  return 'Outstanding';
};

const LEVEL_STYLES = {
  'Below Expectation': { color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
  Developing:         { color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
  Satisfactory:       { color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
  Outstanding:        { color: '#10b981', bg: '#f0fdf4', border: '#6ee7b7' },
};

const getStyle = (score) => LEVEL_STYLES[getPerformanceLevel(score)] || {};

/* ─── Primitive UI ──────────────────────────────────────────────────────────── */

const Card = ({ children, className = '' }) => (
  <div className={`ir-card ${className}`}>{children}</div>
);
const CardHeader = ({ children }) => <div className="ir-card-header">{children}</div>;
const CardTitle = ({ children }) => <h3 className="ir-card-title">{children}</h3>;
const CardContent = ({ children, className = '' }) => (
  <div className={`ir-card-content ${className}`}>{children}</div>
);

const PerformanceBadge = ({ score }) => {
  const level = getPerformanceLevel(score);
  const s = LEVEL_STYLES[level] || {};
  return (
    <span
      className="ir-badge"
      style={{ color: s.color, backgroundColor: s.bg, borderColor: s.border }}
    >
      {level}
    </span>
  );
};

/* ─── Gauge ─────────────────────────────────────────────────────────────────── */
// Half-donut: semicircle from (-10, 60) to (130, 60), radius 70
// arc-length ≈ π × 70 ≈ 219.9
const ARC_LEN = 219.9;

const RoleFitGauge = ({ score }) => {
  const s = getStyle(score);
  const filled = (score / 100) * ARC_LEN;
  return (
    <div className="ir-gauge-wrap">
      <svg viewBox="0 0 140 80" className="ir-gauge-svg" aria-hidden="true">
        {/* Track */}
        <path
          d="M 10 72 A 60 60 0 0 1 130 72"
          stroke="#e5e7eb"
          strokeWidth="14"
          fill="none"
          strokeLinecap="round"
        />
        {/* Fill */}
        <path
          d="M 10 72 A 60 60 0 0 1 130 72"
          stroke={s.color || '#e5e7eb'}
          strokeWidth="14"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${filled} ${ARC_LEN}`}
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
      </svg>
      <div className="ir-gauge-label">
        <span className="ir-gauge-pct" style={{ color: s.color }}>{score}%</span>
        <span className="ir-gauge-caption">Score</span>
      </div>
    </div>
  );
};

/* ─── Evaluation Measurement Tab ────────────────────────────────────────────── */

const EvaluationMeasurement = ({ employee, criteriaOpen, setCriteriaOpen }) => {
  const evalData = employee?.evaluation;
  const hasData =
    evalData &&
    Object.values(evalData).some((cat) => Object.values(cat).some((arr) => arr?.length > 0));

  const LEVELS = [
    { key: 'belowExpectation', label: 'Below Expectation', color: '#dc2626' },
    { key: 'developing',       label: 'Development Areas',       color: '#f59e0b' },
    { key: 'satisfactory',     label: 'Satisfactory Performance', color: '#3b82f6' },
    { key: 'outstanding',      label: 'Outstanding Performance',  color: '#10b981' },
  ];

  return (
    <>
      {/* ── Evaluation Criteria (accordion) ── */}
      <Card className="ir-accordion-card">
        <button
          className="ir-accordion-trigger no-print"
          onClick={() => setCriteriaOpen(!criteriaOpen)}
          aria-expanded={criteriaOpen}
        >
          <span className="ir-accordion-title">Evaluation Criteria</span>
          {criteriaOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        {/* Always in DOM – toggled by CSS; always visible in print */}
        <div className={`ir-accordion-body ${criteriaOpen ? 'open' : ''}`}>
          <CardContent>
            <div className="ir-table-scroll">
              <table className="ir-criteria-table">
                <thead>
                  <tr>
                    <th>Score Against Target</th>
                    <th>Evaluation</th>
                    <th>Interpretation</th>
                  </tr>
                </thead>
                <tbody>
                  {EVALUATION_CRITERIA.map((c, i) => (
                    <tr key={i}>
                      <td>
                        <span
                          className="ir-scale-pill"
                          style={{ color: c.color, backgroundColor: c.bg, borderColor: c.color + '60' }}
                        >
                          {c.scale}
                        </span>
                      </td>
                      <td>
                        <span className="ir-eval-label" style={{ color: c.color }}>
                          {c.label}
                        </span>
                        <br />
                        <span className="ir-eval-desc">{c.description}</span>
                      </td>
                      <td className="ir-interpretation">{c.interpretation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </div>
      </Card>

      {/* ── Performance Evaluation Report ── */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Evaluation Report</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="ir-table-scroll">
            <table className="ir-eval-table">
              <thead>
                <tr>
                  <th className="ir-th-group">Competency Group</th>
                  {LEVELS.map((l) => (
                    <th key={l.key} style={{ borderTop: `3px solid ${l.color}` }}>
                      {l.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {hasData ? (
                  ['core', 'technical'].map((type) => (
                    <tr key={type}>
                      <td className="ir-group-cell">
                        {type === 'core' ? 'Core Competencies' : 'Technical Competencies'}
                      </td>
                      {LEVELS.map((l) => {
                        const items = evalData[type]?.[l.key] || [];
                        return (
                          <td key={l.key}>
                            {items.length > 0 ? (
                              <ul className="ir-comp-list">
                                {items.map((comp, idx) => (
                                  <li key={idx}>{comp}</li>
                                ))}
                              </ul>
                            ) : (
                              <span className="ir-no-items">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={LEVELS.length + 1} className="ir-empty-cell">
                      No evaluation data available for this employee.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

/* ─── Evaluation Score Tab ──────────────────────────────────────────────────── */

const EvaluationScore = ({ employee }) => {
  const coreAvg = calculateAverage(employee.competencies?.core);
  const techAvg  = calculateAverage(employee.competencies?.technical);
  const roleFit  = Math.round((coreAvg + techAvg) / 2);

  const groupAverages = CORE_COMPETENCY_GROUPS.map((g) => ({
    name: g.name,
    avg: calculateAverage(
      (employee.competencies?.core || []).filter((c) => g.competencies.includes(c.name))
    ),
  }));

  return (
    <>
      {/* ── Performance Summary ── */}
      <Card className="ir-summary-card">
        <CardHeader>
          <CardTitle>Performance Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="ir-summary-layout">
            {/* Left: Gauge */}
            <div className="ir-summary-gauge-col">
              <RoleFitGauge score={roleFit} />
              <PerformanceBadge score={roleFit} />
            </div>

            <div className="ir-summary-divider" />

            {/* Right: Core + Technical bars */}
            <div className="ir-summary-metrics">
              {[
                { label: 'Core Competencies',      score: coreAvg },
                { label: 'Technical Competencies', score: techAvg  },
              ].map(({ label, score }) => {
                const s = getStyle(score);
                return (
                  <div key={label} className="ir-metric-row">
                    <div className="ir-metric-meta">
                      <span className="ir-metric-label">{label}</span>
                      <PerformanceBadge score={score} />
                    </div>
                    <div className="ir-metric-bar-wrap">
                      <div className="ir-metric-bar">
                        <div
                          className="ir-metric-fill"
                          style={{ width: `${score}%`, backgroundColor: s.color }}
                        />
                      </div>
                      <span className="ir-metric-pct" style={{ color: s.color }}>{score}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Core Competency Groups ── */}
      <Card>
        <CardHeader>
          <CardTitle>Core Competency Groups</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="ir-groups">
            {groupAverages.map(({ name, avg }, i) => {
              const s = getStyle(avg);
              return (
                <div key={i} className="ir-group-row">
                  <div className="ir-group-meta">
                    <span className="ir-group-name">{name}</span>
                    <PerformanceBadge score={avg} />
                  </div>
                  <div className="ir-group-bar-wrap">
                    <div className="ir-group-bar">
                      <div
                        className="ir-group-fill"
                        style={{ width: `${avg}%`, backgroundColor: s.color }}
                      />
                    </div>
                    <span className="ir-group-pct">{avg}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ── Detailed Competency Scores ── */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Competency Scores</CardTitle>
        </CardHeader>
        <CardContent>
          {[
            { key: 'core',      label: 'Core Competencies' },
            { key: 'technical', label: 'Technical Competencies' },
          ].map(({ key, label }) => (
            <div key={key} className="ir-detail-section">
              <h4 className="ir-section-heading">{label}</h4>
              <div className="ir-competency-grid">
                {(employee.competencies?.[key] || []).map((comp, idx) => {
                  const s = getStyle(comp.score);
                  return (
                    <div
                      key={idx}
                      className="ir-comp-card"
                      style={{ borderLeft: `4px solid ${s.color}` }}
                    >
                      <div className="ir-comp-top">
                        <span className="ir-comp-name">{comp.name}</span>
                        <span className="ir-comp-pct" style={{ color: s.color }}>
                          {comp.score}%
                        </span>
                      </div>
                      <div className="ir-comp-bar">
                        <div
                          className="ir-comp-fill"
                          style={{ width: `${comp.score}%`, backgroundColor: s.color }}
                        />
                      </div>
                      <span className="ir-comp-level" style={{ color: s.color }}>
                        {getPerformanceLevel(comp.score)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </>
  );
};

/* ─── Period / Dept helpers ─────────────────────────────────────────────────── */

const PERIOD_MAP = {
  'Q1: January \u2013 March':    { quarter: 'q1', quarterLabel: 'Q1 (Jan \u2013 Mar)' },
  'Q2: April \u2013 June':       { quarter: 'q2', quarterLabel: 'Q2 (Apr \u2013 Jun)' },
  'Q3: July \u2013 September':   { quarter: 'q3', quarterLabel: 'Q3 (Jul \u2013 Sep)' },
  'Q4: October \u2013 December': { quarter: 'q4', quarterLabel: 'Q4 (Oct \u2013 Dec)' },
};

function parsePeriod(periodStr, createdAt) {
  const mapped = PERIOD_MAP[periodStr] || { quarter: '', quarterLabel: periodStr };
  const year = createdAt
    ? String(new Date(createdAt).getFullYear())
    : String(new Date().getFullYear());
  return { ...mapped, year };
}

function deptNameToSlug(name = '') {
  const map = {
    'Finance':              'finance',
    'Programs':             'programs',
    'Business Development': 'business-development',
    'Digital Health':       'digital-health',
    'People and Culture':     'people-culture',
  };
  return map[name] || name.toLowerCase().replace(/[\s&]+/g, '-');
}

/* ─── PDF Report Generator ──────────────────────────────────────────────────── */

function generateIndividualPDFReport(emp) {
  // eslint-disable-next-line new-cap
  const doc = new jsPDF('portrait', 'mm', 'a4');
  const PW  = 210;
  const PH  = 297;
  const ML  = 14;
  const MR  = 14;
  const CW  = PW - ML - MR; // 182 mm

  // ── Colour palette ────────────────────────────────────────────────────────
  const GREEN = [0, 107, 84];
  const WHITE = [255, 255, 255];
  const DARK  = [31, 41, 55];
  const GRAY  = [107, 114, 128];
  const LGRAY = [229, 231, 235];
  const BG    = [248, 249, 250];

  const PERF_COLOR = {
    'Outstanding':       [16, 185, 129],
    'Satisfactory':      [59, 130, 246],
    'Developing':        [245, 158, 11],
    'Below Expectation': [220, 38, 38],
  };
  const PERF_LIGHT = {
    'Outstanding':       [209, 250, 229],
    'Satisfactory':      [219, 234, 254],
    'Developing':        [254, 243, 199],
    'Below Expectation': [254, 226, 226],
  };

  const lColor = (score) => PERF_COLOR[getPerformanceLevel(score)] || GRAY;
  const lLight = (score) => PERF_LIGHT[getPerformanceLevel(score)] || LGRAY;

  // ── Pre-compute scores ────────────────────────────────────────────────────
  const coreAvg  = calculateAverage(emp.competencies?.core);
  const techAvg  = calculateAverage(emp.competencies?.technical);
  const roleFit  = Math.round((coreAvg + techAvg) / 2);
  const printDate = new Date().toLocaleDateString('en-GB', {
    day: '2-digit', month: 'long', year: 'numeric',
  });

  // ── Drawing helpers ───────────────────────────────────────────────────────
  const sf = (...rgb) => doc.setFillColor(...rgb);
  const st = (...rgb) => doc.setTextColor(...rgb);
  const sd = (...rgb) => doc.setDrawColor(...rgb);

  const progressBar = (x, barY, w, h, score) => {
    const r  = h / 2;
    const lc = lColor(score);
    sf(...LGRAY);
    doc.roundedRect(x, barY, w, h, r, r, 'F');
    sf(...lc);
    const fw = Math.max((score / 100) * w, h);
    doc.roundedRect(x, barY, fw, h, r, r, 'F');
  };

  const sectionBar = (title, sY) => {
    st(...GREEN);
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'bold');
    doc.text(title.toUpperCase(), ML, sY + 4);
    sf(...GREEN);
    doc.rect(ML, sY + 6, CW, 0.4, 'F');
    return sY + 12;
  };

  const addFooter = (page, total) => {
    sf(...GREEN);
    doc.rect(0, PH - 10, PW, 10, 'F');
    st(...WHITE);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('TalentScope360  ·  Individual Performance Report  ·  Confidential', ML, PH - 4);
    doc.text(`Page ${page} of ${total}`, PW - MR, PH - 4, { align: 'right' });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // PAGE 1 — branded header, employee card, summary, competency group bars
  // ─────────────────────────────────────────────────────────────────────────

  sf(...GREEN);
  doc.rect(0, 0, PW, 30, 'F');
  sf(0, 139, 109);
  doc.rect(0, 30, PW, 1.5, 'F');

  st(...WHITE);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('TalentScope360', ML, 13);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Individual Performance Report', ML, 22);
  doc.setFontSize(8);
  doc.text(`Generated: ${printDate}`, PW - MR, 22, { align: 'right' });

  let y = 38;

  // ── Employee card ─────────────────────────────────────────────────────────
  sf(...BG);
  doc.roundedRect(ML, y, CW, 32, 3, 3, 'F');
  sd(...LGRAY);
  doc.setLineWidth(0.3);
  doc.roundedRect(ML, y, CW, 32, 3, 3, 'S');

  sf(...GREEN);
  doc.circle(ML + 16, y + 16, 10, 'F');
  const initials = emp.name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase();
  st(...WHITE);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(initials, ML + 16, y + 18, { align: 'center' });

  st(...DARK);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(emp.name, ML + 30, y + 11);
  st(55, 65, 81);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(emp.jobTitle, ML + 30, y + 19);
  st(...GRAY);
  doc.setFontSize(8.5);
  const periodStr = [emp.department, emp.quarterLabel, emp.year].filter(Boolean).join('  ·  ');
  doc.text(periodStr, ML + 30, y + 27);

  const spX = PW - MR - 38;
  sf(...lLight(roleFit));
  doc.roundedRect(spX, y + 5, 38, 22, 3, 3, 'F');
  st(...GRAY);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('TALENT SCORE', spX + 19, y + 10, { align: 'center' });
  st(...lColor(roleFit));
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(`${roleFit}%`, spX + 19, y + 20, { align: 'center' });
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.text(getPerformanceLevel(roleFit).toUpperCase(), spX + 19, y + 26, { align: 'center' });

  y += 40;

  // ── Performance Summary ───────────────────────────────────────────────────
  y = sectionBar('Performance Summary', y);

  const metricRow = (label, score, rowY) => {
    const lc = lColor(score);
    const lb = lLight(score);
    const ll = getPerformanceLevel(score);

    st(...DARK);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.text(label, ML, rowY + 4);

    const badgeW = 32;
    const badgeX = ML + 72;
    sf(...lb);
    doc.roundedRect(badgeX, rowY, badgeW, 6.5, 1.5, 1.5, 'F');
    st(...lc);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text(ll, badgeX + badgeW / 2, rowY + 4.5, { align: 'center' });

    st(...lc);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`${score}%`, PW - MR, rowY + 4, { align: 'right' });

    progressBar(ML, rowY + 8, CW, 5, score);
    return rowY + 19;
  };

  y = metricRow('Core Competencies', coreAvg, y);
  y = metricRow('Technical Competencies', techAvg, y);
  y += 6;

  // ── Core Competency Groups ────────────────────────────────────────────────
  y = sectionBar('Core Competency Groups', y);

  const groupAverages = CORE_COMPETENCY_GROUPS.map((g) => ({
    name: g.name,
    avg: calculateAverage(
      (emp.competencies?.core || []).filter((c) => g.competencies.includes(c.name))
    ),
  }));

  groupAverages.forEach(({ name, avg }) => {
    const lc = lColor(avg);
    st(...DARK);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.text(name, ML, y + 3.5);
    st(...lc);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.text(`${avg}%`, PW - MR, y + 3.5, { align: 'right' });
    progressBar(ML, y + 5.5, CW, 4.5, avg);
    y += 14;
  });

  y += 6;

  // ─────────────────────────────────────────────────────────────────────────
  // Detailed Competency Scores — one table per category
  // ─────────────────────────────────────────────────────────────────────────
  if (y > 195) { doc.addPage(); y = 20; }

  y = sectionBar('Detailed Competency Scores', y);

  ['core', 'technical'].forEach((key) => {
    const comps = emp.competencies?.[key] || [];
    if (comps.length === 0) return;

    const subLabel = key === 'core' ? 'Core Competencies' : 'Technical Competencies';
    st(75, 85, 99);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.text(subLabel.toUpperCase(), ML, y + 3);
    y += 7;

    doc.autoTable({
      startY: y,
      head: [['Competency', 'Score', 'Performance Level']],
      body: comps.map((c) => [c.name, c.score, getPerformanceLevel(c.score)]),
      theme: 'grid',
      styles: {
        fontSize: 8.5,
        font: 'helvetica',
        cellPadding: { top: 3, right: 5, bottom: 3, left: 5 },
        textColor: DARK,
        lineColor: LGRAY,
        lineWidth: 0.25,
      },
      headStyles: {
        fillColor: GREEN,
        textColor: WHITE,
        fontStyle: 'bold',
        fontSize: 8.5,
        cellPadding: { top: 4, right: 5, bottom: 4, left: 5 },
      },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: 24, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 58 },
      },
      margin: { left: ML, right: MR, bottom: 12 },
      alternateRowStyles: { fillColor: [250, 250, 252] },
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        const score = data.row.raw[1];
        const lc = lColor(score);
        if (data.column.index === 1) {
          data.cell.styles.textColor = lc;
          data.cell.text = [`${score}%`];
        }
        if (data.column.index === 2) {
          data.cell.styles.textColor = lc;
        }
      },
    });

    y = doc.lastAutoTable.finalY + 8;
    if (y > 245) { doc.addPage(); y = 20; }
  });

  // ─────────────────────────────────────────────────────────────────────────
  // Performance Evaluation Report — categorised competencies
  // ─────────────────────────────────────────────────────────────────────────
  if (y > 175) { doc.addPage(); y = 20; }

  y = sectionBar('Performance Evaluation Report', y);

  const evalData = emp.evaluation;
  const hasEvalData =
    evalData &&
    Object.values(evalData).some((cat) => Object.values(cat).some((arr) => arr?.length > 0));

  const EVAL_LEVELS = [
    { key: 'belowExpectation', label: 'Below Expectation', color: [220, 38, 38] },
    { key: 'developing',       label: 'Development Areas', color: [245, 158, 11] },
    { key: 'satisfactory',     label: 'Satisfactory',      color: [59, 130, 246] },
    { key: 'outstanding',      label: 'Outstanding',       color: [16, 185, 129] },
  ];

  if (hasEvalData) {
    doc.autoTable({
      startY: y,
      head: [['Competency Type', ...EVAL_LEVELS.map((l) => l.label)]],
      body: ['core', 'technical'].map((type) => [
        type === 'core' ? 'Core Competencies' : 'Technical Competencies',
        ...EVAL_LEVELS.map((l) => {
          const items = evalData[type]?.[l.key] || [];
          return items.length > 0 ? items.join('\n') : '—';
        }),
      ]),
      theme: 'grid',
      styles: {
        fontSize: 8,
        font: 'helvetica',
        cellPadding: { top: 4, right: 5, bottom: 4, left: 5 },
        textColor: DARK,
        lineColor: LGRAY,
        lineWidth: 0.25,
        valign: 'top',
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: GREEN,
        textColor: WHITE,
        fontStyle: 'bold',
        fontSize: 8,
      },
      columnStyles: {
        0: { cellWidth: 36, fontStyle: 'bold', fillColor: BG },
        1: { cellWidth: 36.5 },
        2: { cellWidth: 36.5 },
        3: { cellWidth: 36.5 },
        4: { cellWidth: 36.5 },
      },
      margin: { left: ML, right: MR, bottom: 12 },
      didParseCell: (data) => {
        if (data.section === 'head' && data.column.index > 0) {
          data.cell.styles.fillColor = EVAL_LEVELS[data.column.index - 1].color;
        }
      },
    });
    y = doc.lastAutoTable.finalY + 10;
  } else {
    st(...GRAY);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text('No evaluation data available.', ML, y + 5);
    y += 14;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Evaluation Criteria legend
  // ─────────────────────────────────────────────────────────────────────────
  if (y > 195) { doc.addPage(); y = 20; }

  y = sectionBar('Evaluation Criteria', y);

  doc.autoTable({
    startY: y,
    head: [['Score Range', 'Level', 'Description', 'Interpretation']],
    body: EVALUATION_CRITERIA.map((c) => [c.scale, c.label, c.description, c.interpretation]),
    theme: 'grid',
    styles: {
      fontSize: 7.5,
      font: 'helvetica',
      cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
      textColor: DARK,
      lineColor: LGRAY,
      lineWidth: 0.25,
      valign: 'top',
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: GREEN,
      textColor: WHITE,
      fontStyle: 'bold',
      fontSize: 8,
    },
    columnStyles: {
      0: { cellWidth: 22, halign: 'center' },
      1: { cellWidth: 28, fontStyle: 'bold' },
      2: { cellWidth: 60 },
      3: { cellWidth: 72 },
    },
    margin: { left: ML, right: MR, bottom: 12 },
    didParseCell: (data) => {
      if (data.section !== 'body' || data.column.index !== 1) return;
      const levelColorMap = {
        'Below Expectation': [220, 38, 38],
        'Developing':        [245, 158, 11],
        'Satisfactory':      [59, 130, 246],
        'Outstanding':       [16, 185, 129],
      };
      data.cell.styles.textColor = levelColorMap[data.cell.raw] || DARK;
    },
  });

  // ── Stamp footer on every page ────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    addFooter(p, totalPages);
  }

  const safeName = emp.name.replace(/[^a-zA-Z0-9]/g, '_');
  const safeDate = new Date().toISOString().split('T')[0];
  doc.save(`Individual_Report_${safeName}_${safeDate}.pdf`);
}

/* ─── Main Component ────────────────────────────────────────────────────────── */

const IndividualReports = () => {
  const { organizationId } = useAuth();

  const [selectedDept,     setSelectedDept]     = useState('');
  const [selectedQuarter,  setSelectedQuarter]  = useState('');
  const [selectedYear,     setSelectedYear]     = useState('');
  const [searchQuery,      setSearchQuery]      = useState('');
  const [allReports,       setAllReports]       = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDetail,       setShowDetail]       = useState(false);
  const [criteriaOpen,     setCriteriaOpen]     = useState(false);
  const [activeTab,        setActiveTab]        = useState('measurement');
  const [listLoading,      setListLoading]      = useState(true);
  const [detailLoading,    setDetailLoading]    = useState(false);

  useEffect(() => {
    fetchReports();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const fetchReports = async () => {
    setListLoading(true);
    try {
      let query = supabase
        .from('evaluations')
        .select(`
          evaluation_id,
          employee_name,
          job_title,
          evaluation_period,
          evaluation_type,
          created_at,
          department:department_id ( depart_name )
        `)
        .eq('status', 'Complete')
        .order('employee_name');

      if (organizationId) query = query.eq('organization_id', organizationId);

      const { data: evals, error } = await query;
      if (error || !evals) return;

      // Group by employee + period → one list row per person per quarter
      const grouped = {};
      evals.forEach(ev => {
        const key = `${ev.employee_name}__${ev.evaluation_period}`;
        if (!grouped[key]) {
          const { quarter, quarterLabel, year } = parsePeriod(ev.evaluation_period, ev.created_at);
          grouped[key] = {
            id: key,
            name: ev.employee_name,
            jobTitle: ev.job_title || '—',
            department: ev.department?.depart_name || '—',
            departmentId: deptNameToSlug(ev.department?.depart_name),
            quarter,
            quarterLabel,
            year,
            _coreEvalId: null,
            _techEvalId: null,
            competencies: { core: [], technical: [] },
            evaluation:   {
              core:      { belowExpectation: [], developing: [], satisfactory: [], outstanding: [] },
              technical: { belowExpectation: [], developing: [], satisfactory: [], outstanding: [] },
            },
          };
        }
        if (ev.evaluation_type === 'Core Competency Evaluation') {
          grouped[key]._coreEvalId = ev.evaluation_id;
        } else if (ev.evaluation_type === 'Technical Competency Evaluation') {
          grouped[key]._techEvalId = ev.evaluation_id;
        }
      });

      setAllReports(Object.values(grouped));
    } finally {
      setListLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return allReports.filter((emp) => {
      if (selectedDept    && emp.departmentId !== selectedDept)    return false;
      if (selectedQuarter && emp.quarter       !== selectedQuarter) return false;
      if (selectedYear    && emp.year          !== selectedYear)    return false;
      if (q && !emp.name.toLowerCase().includes(q) && !emp.jobTitle.toLowerCase().includes(q))
        return false;
      return true;
    });
  }, [allReports, selectedDept, selectedQuarter, selectedYear, searchQuery]);

  const handleViewReport = async (emp) => {
    setCriteriaOpen(false);
    setActiveTab('measurement');
    setDetailLoading(true);
    setShowDetail(true);

    const evalIds = [emp._coreEvalId, emp._techEvalId].filter(Boolean);

    if (evalIds.length > 0) {
      const { data: ratings } = await supabase
        .from('evaluation_ratings')
        .select(`
          evaluation_id,
          rating,
          competency:competency_id ( competency_name )
        `)
        .in('evaluation_id', evalIds)
        .not('rating', 'is', null);

      if (ratings) {
        const coreComps = [];
        const techComps = [];

        ratings.forEach(r => {
          if (!r.rating || !r.competency?.competency_name) return;
          const score = Math.round((r.rating / 5) * 100);
          const item = { name: r.competency.competency_name, score };
          if (r.evaluation_id === emp._coreEvalId) coreComps.push(item);
          else techComps.push(item);
        });

        const categorise = (comps) => ({
          belowExpectation: comps.filter(c => c.score < 65).map(c => c.name),
          developing:       comps.filter(c => c.score >= 65 && c.score < 75).map(c => c.name),
          satisfactory:     comps.filter(c => c.score >= 75 && c.score < 90).map(c => c.name),
          outstanding:      comps.filter(c => c.score >= 90).map(c => c.name),
        });

        emp = {
          ...emp,
          competencies: { core: coreComps, technical: techComps },
          evaluation:   { core: categorise(coreComps), technical: categorise(techComps) },
        };
      }
    }

    setSelectedEmployee(emp);
    setDetailLoading(false);
  };

  const handleBack = () => {
    setShowDetail(false);
    setSelectedEmployee(null);
  };

  const handleDownload = () => generateIndividualPDFReport(selectedEmployee);

  /* ── Detail View ── */
  if (showDetail && selectedEmployee) {
    const emp = selectedEmployee;
    const coreAvg = calculateAverage(emp.competencies?.core);
    const techAvg  = calculateAverage(emp.competencies?.technical);
    const roleFit  = Math.round((coreAvg + techAvg) / 2);
    const roleFitStyle = getStyle(roleFit);
    const printDate = new Date().toLocaleDateString('en-GB', {
      day: '2-digit', month: 'long', year: 'numeric',
    });

    return (
      <div className="ir-container ir-detail-mode">
        {/* Print-only header */}
        <div className="ir-print-header print-only">
          <div className="ir-print-org">TalentScope360</div>
          <div className="ir-print-title">Individual Performance Report</div>
          <div className="ir-print-meta">Generated: {printDate}</div>
        </div>

        <div className="ir-page-header">
          <h1><i className="bi bi-person" />Individual Performance Reports</h1>
          <p>Track individual performance with insights into competency strengths and trends over time.</p>
          <hr />
        </div>

        <div className="ir-detail-view">
          {detailLoading && (
            <div className="ir-detail-loading">
              <div className="corp-spinner" />
              <span>Loading report…</span>
            </div>
          )}

          {/* Employee Banner */}
          <Card className="ir-banner-card">
            <CardContent className="ir-banner-content">
              <div className="ir-avatar-circle">
                <User size={28} />
              </div>
              <div className="ir-banner-info">
                <h2 className="ir-banner-name">{emp.name}</h2>
                <p className="ir-banner-title">{emp.jobTitle}</p>
                <p className="ir-banner-dept">{emp.department}</p>
                {(emp.quarterLabel || emp.year) && (
                  <p className="ir-banner-period">
                    {[emp.quarterLabel, emp.year].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
              <div className="ir-banner-rolefit">
                <Target size={16} style={{ color: roleFitStyle.color }} />
                <span className="ir-rolefit-label">Talent Score</span>
                <span className="ir-rolefit-pct" style={{ color: roleFitStyle.color }}>
                  {roleFit}%
                </span>
                <PerformanceBadge score={roleFit} />
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Card className="ir-tabs-card no-print">
            <div className="ir-tabs">
              {[
                { key: 'measurement', label: 'Talent Score' },
                { key: 'score',       label: 'Talent Evaluation' },
              ].map((t) => (
                <button
                  key={t.key}
                  className={`ir-tab ${activeTab === t.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </Card>

          {/* Tab Content — both rendered; CSS controls visibility; print shows both */}
          <div className={`ir-tab-pane ${activeTab === 'measurement' ? 'active' : ''}`}>
            <EvaluationMeasurement
              employee={emp}
              criteriaOpen={criteriaOpen}
              setCriteriaOpen={setCriteriaOpen}
            />
          </div>
          <div className={`ir-tab-pane ${activeTab === 'score' ? 'active' : ''}`}>
            <EvaluationScore employee={emp} />
          </div>

          {/* Action Buttons */}
          <div className="ir-action-row no-print">
            <button className="ir-btn ir-btn-back" onClick={handleBack}>
              <ArrowLeft size={16} />
              Back to Reports
            </button>
            <button className="ir-btn ir-btn-download" onClick={handleDownload}>
              <Download size={16} />
              Download Report
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── List View ── */
  return (
    <div className="ir-container">
      <div className="ir-page-header">
        <h2><i className="bi bi-person" />Individual Performance Reports</h2>
        <p>Track individual performance with insights into competency strengths and trends over time.</p>
        <hr />
      </div>

      {/* Search + Filters */}
      <div className="ir-controls">
        <div className="ir-search-wrap">
          <Search size={15} className="ir-search-icon" />
          <input
            type="text"
            className="ir-search-input"
            placeholder="Search by name or job title…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="ir-filter-row">
          {[
            { label: 'Department', value: selectedDept,    setValue: setSelectedDept,    options: DEPARTMENTS },
            { label: 'Period',     value: selectedQuarter, setValue: setSelectedQuarter, options: QUARTERS    },
            { label: 'Year',       value: selectedYear,    setValue: setSelectedYear,    options: YEARS       },
          ].map(({ label, value, setValue, options }) => (
            <div key={label} className="ir-filter-group">
              <label className="ir-filter-label">{label}</label>
              <select
                className="ir-filter-select"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              >
                {options.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>

      {/* Results count */}
      <p className="ir-results-count">
        {listLoading ? 'Loading reports…' : `${filtered.length} report${filtered.length !== 1 ? 's' : ''} found`}
      </p>

      {/* Table */}
      <div className="ir-table-wrap">
        <table className="ir-table">
          <thead>
            <tr>
              <th>Employee Name</th>
              <th>Job Title</th>
              <th>Department</th>
              <th>Period</th>
              <th>Year</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length > 0 ? (
              filtered.map((emp) => (
                <tr key={emp.id}>
                  <td>
                    <div className="ir-name-cell">
                      <div className="ir-table-avatar"><User size={13} /></div>
                      <span>{emp.name}</span>
                    </div>
                  </td>
                  <td>{emp.jobTitle}</td>
                  <td>{emp.department || '—'}</td>
                  <td>{emp.quarterLabel || '—'}</td>
                  <td>{emp.year        || '—'}</td>
                  <td>
                    <button
                      className="ir-view-btn"
                      onClick={() => handleViewReport(emp)}
                    >
                      <FileText size={13} />
                      View Report
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6">
                  <div className="ir-empty-state">
                    <FileText size={38} className="ir-empty-icon" />
                    <p className="ir-empty-title">No reports found</p>
                    <span className="ir-empty-sub">
                      Try adjusting your search or filter criteria
                    </span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IndividualReports;
